import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useFilters } from "@/context/FiltersContext";
import useFollowing from "@/lib/useFollowing";
import { toast } from "sonner";
import { Loader2, Check, Search, Star, Heart } from "lucide-react";

const WATCH_EVENTS = [
  "Bareback", "Saddle Bronc", "Bull Riding", "Tie-Down Roping",
  "Steer Wrestling", "Team Roping (Header)", "Team Roping (Heeler)", "Ladies Barrel Racing", "Ladies Breakaway Roping",
];

const COUNTRIES = [
  { v: "Canada", l: "Canada", d: "CPRA & Canadian pro rodeo" },
  { v: "United States", l: "USA", d: "PRCA & US pro rodeo" },
  { v: "", l: "Both", d: "Follow the action everywhere" },
];

export default function FanOnboarding() {
  const { user } = useAuth();
  const { setCountry } = useFilters();
  const { following, isFollowing, follow } = useFollowing();
  const [show, setShow] = useState(false);
  const [country, setCtry] = useState("");
  const [events, setEvents] = useState(new Set());
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    api.get("/me/profile").then((r) => { if (!r.data.onboarded) setShow(true); }).catch(() => {});
  }, []);

  useEffect(() => {
    clearTimeout(timer.current);
    if (q.trim().length < 2) { setResults([]); return; }
    timer.current = setTimeout(async () => {
      setSearching(true);
      try { const { data } = await api.get("/competitors/search", { params: { q: q.trim() } }); setResults(data || []); }
      catch { setResults([]); }
      finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(timer.current);
  }, [q]);

  const toggleEvent = (e) => setEvents((p) => { const n = new Set(p); n.has(e) ? n.delete(e) : n.add(e); return n; });

  const finish = async () => {
    setSaving(true);
    try {
      setCountry(country);
      await api.post("/me/events", { events: [...events] }); // marks onboarded
      toast.success("You're all set!");
      setShow(false);
    } catch { toast.error("Couldn't save — try again"); }
    finally { setSaving(false); }
  };

  if (!show) return null;
  return (
    <div className="fixed inset-0 z-[3000] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" data-testid="fan-onboarding-modal">
      <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden flex flex-col max-h-[90dvh]">
        <div className="bg-[#0C1017] text-white px-6 py-5 shrink-0">
          <div className="text-[11px] tracking-[3px] font-bold text-[#F26D77]">WELCOME, RODEO FAN</div>
          <div className="font-display text-2xl font-extrabold mt-1">Let's set up your feed</div>
          <div className="text-sm text-slate-300 mt-1">Follow your favorites and we'll keep you on every run.</div>
        </div>

        <div className="p-6 space-y-6 overflow-y-auto">
          {/* Country */}
          <div>
            <div className="text-sm font-bold text-[#14181F] mb-2">Where do you follow rodeo?</div>
            <div className="grid grid-cols-3 gap-2">
              {COUNTRIES.map((c) => {
                const on = country === c.v;
                return (
                  <button key={c.l} onClick={() => setCtry(c.v)} data-testid={`fan-country-${c.l}`}
                    className={`text-left px-3 py-2.5 rounded-lg border ${on ? "bg-[#FBF4F5] border-[#C8000F] text-[#9E0008]" : "bg-white border-[#D7DBE0] text-slate-600 hover:border-slate-400"}`}>
                    <div className="text-sm font-bold flex items-center gap-1">{c.l}{on && <Check className="w-3.5 h-3.5" />}</div>
                    <div className="text-[10px] opacity-70 leading-tight mt-0.5">{c.d}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Favorite riders */}
          <div>
            <div className="text-sm font-bold text-[#14181F] mb-2 flex items-center gap-1.5"><Heart className="w-4 h-4 text-[#C8000F]" /> Follow your favorite riders</div>
            {following.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2" data-testid="fan-onboarding-following">
                {following.map((n) => (
                  <span key={n} className="inline-flex items-center gap-1 text-[12px] px-2 py-1 rounded-md bg-[#C8000F]/10 text-[#9E0008] border border-[#C8000F]/30">
                    <Star className="w-3 h-3 fill-current" /> {n}
                  </span>
                ))}
              </div>
            )}
            <div className="relative">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search riders by name…" data-testid="fan-rider-search"
                className="w-full rounded-lg border border-[#D7DBE0] pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:border-[#C8000F]" />
              {searching && <Loader2 className="w-4 h-4 animate-spin text-slate-400 absolute right-3 top-1/2 -translate-y-1/2" />}
            </div>
            {results.length > 0 && (
              <div className="mt-2 border border-[#E4E7EB] rounded-lg divide-y divide-[#EEF0F2] max-h-52 overflow-y-auto" data-testid="fan-rider-results">
                {results.map((r) => {
                  const on = isFollowing(r.name);
                  return (
                    <div key={r.name} className="flex items-center justify-between gap-2 px-3 py-2">
                      <div className="min-w-0">
                        <div className="text-sm font-semibold text-[#14181F] truncate">{r.name}</div>
                        {r.hometown && <div className="text-[11px] text-slate-400 truncate">{r.hometown}</div>}
                      </div>
                      <button disabled={on} onClick={() => follow(r.name)} data-testid={`fan-follow-${r.name.replace(/\s/g, "-")}`}
                        className={`shrink-0 text-xs font-semibold px-3 py-1.5 rounded-md ${on ? "bg-[#EAF3EC] text-[#166534]" : "bg-[#C8000F] hover:bg-[#9E0008] text-white"}`}>
                        {on ? "Following" : "Follow"}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Events to watch */}
          <div>
            <div className="text-sm font-bold text-[#14181F] mb-2">Which events do you love to watch? <span className="font-normal text-slate-400">(optional)</span></div>
            <div className="grid grid-cols-2 gap-2">
              {WATCH_EVENTS.map((e) => {
                const on = events.has(e);
                return (
                  <button key={e} onClick={() => toggleEvent(e)} data-testid={`fan-event-${e.replace(/\s/g, "-")}`}
                    className={`flex items-center justify-between gap-2 text-sm font-semibold px-3 py-2.5 rounded-lg border text-left ${on ? "bg-[#FBF4F5] border-[#C8000F] text-[#9E0008]" : "bg-white border-[#D7DBE0] text-slate-600 hover:border-slate-400"}`}>
                    {e} {on && <Check className="w-4 h-4 shrink-0" />}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100 shrink-0 bg-white">
          <button onClick={finish} className="text-xs text-slate-400 hover:underline" data-testid="fan-onboarding-skip">Skip for now</button>
          <button onClick={finish} disabled={saving} data-testid="fan-onboarding-finish"
            className="flex items-center gap-2 bg-[#C8000F] hover:bg-[#9E0008] text-white font-bold px-6 py-2.5 rounded-lg disabled:opacity-60">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Start following
          </button>
        </div>
      </div>
    </div>
  );
}
